import * as React from 'react';

import { useAtomValue } from 'jotai';
import { Marker } from 'react-map-gl/maplibre';

import {
  AccountAddress,
  useAccountAddressesQuery,
} from '../../hooks/queries/useAccountAddressesQuery';
import {
  Facility,
  useFacilitiesQuery,
} from '../../hooks/queries/useFacilitiesQuery';
import { useRouteServicesQuery } from '../../hooks/queries/useRouteServicesQuery';
import { useRouteTasksQuery } from '../../hooks/queries/useRouteTasksQuery';
import { $routeForm } from '../../state/routeForm.localState';
import { ServiceRecord } from '../../types/service';
import { TaskRecord } from '../../types/task';

import { MapView } from '~/shared/components/MapView/MapView';

const ROUTE_STOP_COLOR = '#E76627';

type RouteStop = {
  index: number;
  lat: number;
  lng: number;
  label: string;
  tasks: TaskRecord[];
};

function resolveTaskCoordinates(
  task: TaskRecord,
  service: ServiceRecord | undefined,
): { lat: number; lng: number } | undefined {
  if (!service) return undefined;

  if (task.facilityId && service.facilityCoordinates) {
    return {
      lat: service.facilityCoordinates.latitude,
      lng: service.facilityCoordinates.longitude,
    };
  }

  if (task.accountAddressId && service.accountAddressCoordinates) {
    return {
      lat: service.accountAddressCoordinates.latitude,
      lng: service.accountAddressCoordinates.longitude,
    };
  }

  if (service.coordinates) {
    return {
      lat: service.coordinates.latitude,
      lng: service.coordinates.longitude,
    };
  }

  return undefined;
}

function roundCoord(n: number): number {
  return Math.round(n * 10000) / 10000;
}

function resolveTaskLabel(
  task: TaskRecord,
  service: ServiceRecord | undefined,
  facilityMap: Map<string, Facility>,
  addressMap: Map<string, AccountAddress>,
  fallbackIndex: number,
): string {
  if (task.facilityId) {
    const facility = facilityMap.get(task.facilityId);
    if (facility) {
      const addr = facility.address;
      if (facility.facilityName && addr?.streetAddress)
        return `${facility.facilityName} | ${addr.streetAddress}`;
      if (facility.facilityName) return facility.facilityName;
      if (addr?.streetAddress) return addr.streetAddress;
    }
  }

  if (task.accountAddressId) {
    const addr = addressMap.get(task.accountAddressId);
    if (addr) {
      const parts = [addr.streetAddress, addr.city, addr.state]
        .filter(Boolean)
        .join(', ');
      if (addr.account?.name && parts) return `${addr.account.name} | ${parts}`;
      if (parts) return parts;
    }
  }

  const address = service?.address;
  if (address?.streetAddress) return address.streetAddress;
  if (address?.city && address?.state)
    return `${address.city}, ${address.state}`;
  return `Stop ${fallbackIndex}`;
}

function buildStops(
  tasks: TaskRecord[],
  serviceMap: Map<string, ServiceRecord>,
  facilityMap: Map<string, Facility>,
  addressMap: Map<string, AccountAddress>,
): RouteStop[] {
  const stops: RouteStop[] = [];

  for (const task of tasks) {
    const service = serviceMap.get(task.fulfillmentServiceId);
    const coords = resolveTaskCoordinates(task, service);
    if (!coords) continue;

    const roundedLat = roundCoord(coords.lat);
    const roundedLng = roundCoord(coords.lng);

    const lastStop = stops[stops.length - 1];
    if (
      lastStop &&
      roundCoord(lastStop.lat) === roundedLat &&
      roundCoord(lastStop.lng) === roundedLng
    ) {
      lastStop.tasks.push(task);
      continue;
    }

    const label = resolveTaskLabel(
      task,
      service,
      facilityMap,
      addressMap,
      stops.length + 1,
    );

    stops.push({
      index: stops.length + 1,
      lat: coords.lat,
      lng: coords.lng,
      label,
      tasks: [task],
    });
  }

  return stops;
}

type ServiceAssetInfo = {
  assetTypeName: string;
  assetGroup?: number;
  assets: Map<string, string>;
};

function resolveTaskAssetLabel(
  task: TaskRecord,
  serviceAssetMap: Map<string, ServiceAssetInfo[]>,
): string | undefined {
  const serviceAssets = serviceAssetMap.get(task.fulfillmentServiceId);
  if (!serviceAssets?.length) return undefined;

  const taskGroup = task.assetGroup ?? 1;
  const matching =
    serviceAssets.find((a) => (a.assetGroup ?? 1) === taskGroup) ??
    (serviceAssets.length === 1 ? serviceAssets[0] : undefined);
  if (!matching) return undefined;

  if (task.assetId) {
    const assetName = matching.assets.get(task.assetId);
    if (assetName) return `${matching.assetTypeName} (${assetName})`;
  }

  return matching.assetTypeName;
}

type StopMarkerProps = {
  stop: RouteStop;
  serviceAssetMap: Map<string, ServiceAssetInfo[]>;
};

const StopMarker = ({ stop, serviceAssetMap }: StopMarkerProps) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <Marker
      latitude={stop.lat}
      longitude={stop.lng}
      anchor="center"
      className="@StopMarker"
    >
      <div
        className="relative cursor-pointer"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div
          className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-xs font-bold text-white shadow-md"
          style={{ backgroundColor: ROUTE_STOP_COLOR }}
        >
          {stop.index}
        </div>
        {hovered && (
          <div className="text-background absolute bottom-full left-1/2 mb-1 -translate-x-1/2 rounded bg-foreground px-2 py-1 text-xs whitespace-nowrap shadow-lg">
            <div className="font-bold">{stop.label}</div>
            <hr className="border-background/30 my-1" />
            {stop.tasks.map((task) => {
              const assetLabel = resolveTaskAssetLabel(task, serviceAssetMap);
              return (
                <div key={task.id}>
                  {task.type}
                  {assetLabel && <> &middot; {assetLabel}</>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Marker>
  );
};

export const RouteMapTab = () => {
  const form = useAtomValue($routeForm);
  const routeId = form.id ?? '';

  const { data: services = [] } = useRouteServicesQuery({
    routeId,
    queryOptions: { enabled: !!form.id },
  });

  const { data } = useRouteTasksQuery({
    routeId,
    queryOptions: { enabled: !!form.id },
  });
  const tasks = data?.tasks ?? [];

  const { data: facilitiesData } = useFacilitiesQuery({ limit: 500 });
  const { data: addressesData } = useAccountAddressesQuery({ limit: 500 });

  const serviceMap = React.useMemo(
    () => new Map(services.map((s) => [s.id, s])),
    [services],
  );

  const facilityMap = React.useMemo(() => {
    const map = new Map<string, Facility>();
    for (const f of facilitiesData?.facilities ?? []) {
      map.set(f.id, f);
    }
    return map;
  }, [facilitiesData?.facilities]);

  const addressMap = React.useMemo(() => {
    const map = new Map<string, AccountAddress>();
    for (const addr of addressesData?.addresses ?? []) {
      map.set(addr.id, addr);
    }
    return map;
  }, [addressesData?.addresses]);

  const serviceAssetMap = React.useMemo(() => {
    const map = new Map<string, ServiceAssetInfo[]>();
    for (const service of services) {
      const infos: ServiceAssetInfo[] = [];
      const seen = new Set<string>();
      for (const aq of service.assetQuantities ?? []) {
        const key = `${aq.assetType?.id}-${aq.assetGroup}`;
        if (aq.assetType?.name && !seen.has(key)) {
          seen.add(key);
          const assets = new Map<string, string>();
          if (aq.asset?.id && aq.asset.identifier) {
            assets.set(aq.asset.id, aq.asset.identifier);
          }
          infos.push({
            assetTypeName: aq.assetType.name,
            assetGroup: aq.assetGroup,
            assets,
          });
        }
      }
      map.set(service.id, infos);
    }
    return map;
  }, [services]);

  const stops = React.useMemo(
    () => buildStops(tasks, serviceMap, facilityMap, addressMap),
    [tasks, serviceMap, facilityMap, addressMap],
  );

  const fitPoints = React.useMemo(
    () => stops.map((stop) => ({ lat: stop.lat, lng: stop.lng })),
    [stops],
  );

  if (stops.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center text-foreground/40">
        No service locations available
      </div>
    );
  }

  return (
    <div className="@RouteMapTab h-[380px]">
      <MapView markers={[]} fitPoints={fitPoints} className="h-full">
        {stops.map((stop) => (
          <StopMarker
            key={`stop-${stop.index}`}
            stop={stop}
            serviceAssetMap={serviceAssetMap}
          />
        ))}
      </MapView>
    </div>
  );
};
